import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Smartphone, Mail, KeyRound, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackButton } from "@/components/BackButton";

const TABS = [
  { key: "signin", label: "Sign in", icon: KeyRound },
  { key: "signup", label: "Create account", icon: Mail },
  { key: "otp", label: "Mobile OTP", icon: Smartphone },
];

const inputCls =
  "w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal";

export default function Auth() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("signin");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // forgot-password | forgot-id | null
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", identifier: "", code: "" });
  const [otpSent, setOtpSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const switchTab = (t) => { setTab(t); setError(""); setNotice(""); setMode(null); };

  const done = (u) => { setUser(u); navigate("/"); };

  const submit = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (mode === "forgot-password") {
        await api.post("/auth/forgot-password", { email: form.email });
        setNotice("If that email is registered, a reset link is on its way.");
      } else if (mode === "forgot-id") {
        await api.post("/auth/forgot-id", { email: form.email });
        setNotice("If that email is registered, we've sent your sign-in ID to it.");
      } else if (tab === "signin") {
        const r = await api.post("/auth/login", { identifier: form.identifier, password: form.password });
        done(r.data);
      } else if (tab === "signup") {
        const r = await api.post("/auth/register", { name: form.name, email: form.email, mobile: form.mobile, password: form.password });
        done(r.data);
      } else if (tab === "otp") {
        if (!otpSent) {
          await api.post("/auth/otp/send", { mobile: form.mobile });
          setOtpSent(true);
          setNotice("Code sent — check your messages.");
        } else {
          const r = await api.post("/auth/otp/verify", { mobile: form.mobile, code: form.code, name: form.name });
          done(r.data);
        }
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="auth-page" className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden flex-col justify-center bg-charcoal p-12 text-bone lg:flex">
        <div>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-bone/50">Members</p>
          <h1 className="font-serif text-5xl font-light leading-tight tracking-tight">
            Your home,<br />your account.
          </h1>
          <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-bone/60">
            Sign in to track orders, check out faster and keep your gathered pieces in one place.
          </p>
        </div>
        <p className="absolute bottom-12 left-12 text-xs uppercase tracking-[0.2em] text-bone/40">Shop ONline New York — New York's finest, online</p>
      </div>

      <div className="flex items-center justify-center bg-bone px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="w-full max-w-md">
          <BackButton label="Back to store" />
          <Link to="/" data-testid="auth-form-logo" className="mb-10 mt-6 flex justify-center">
            <img src="/logo.webp" alt="Shop ONline New York" className="h-16 w-auto mix-blend-multiply sm:h-20" />
          </Link>

          <div className="mb-8 grid grid-cols-3 gap-2 rounded-full border border-charcoal/15 p-1" data-testid="auth-tabs">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => switchTab(t.key)}
                className={`rounded-full px-2 py-2.5 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tab === t.key && !mode ? "bg-charcoal text-bone" : "text-ash hover:text-charcoal"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {tab === "signup" && !mode && (
              <input data-testid="signup-name" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
            )}
            {(tab === "signup" || mode) && (
              <input data-testid="auth-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
            )}
            {tab === "signin" && !mode && (
              <input data-testid="signin-identifier" placeholder="Email or mobile number" value={form.identifier} onChange={set("identifier")} className={inputCls} />
            )}
            {(tab === "signup" || tab === "otp") && !mode && (
              <input data-testid="auth-mobile" placeholder="Mobile with country code — +1..." value={form.mobile} onChange={set("mobile")} className={inputCls} />
            )}
            {tab === "otp" && !mode && otpSent && (
              <input data-testid="otp-code" placeholder="6-digit code" value={form.code} onChange={set("code")} className={inputCls} />
            )}
            {tab === "otp" && !mode && otpSent && !form.name && (
              <input data-testid="otp-name" placeholder="Your name (first time only)" value={form.name} onChange={set("name")} className={inputCls} />
            )}
            {tab === "otp" && !mode && !otpSent && !error && (
              <p data-testid="otp-note" className="text-xs font-light leading-relaxed text-ash">
                New here? We'll create your account the first time your number verifies.
              </p>
            )}
            {(tab === "signin" || tab === "signup") && !mode && (
              <input data-testid="auth-password" type="password"
                placeholder={tab === "signup" ? "Password — at least 8 characters" : "Password"}
                value={form.password} onChange={set("password")} className={inputCls} />
            )}

            {error && <p data-testid="auth-error" className="text-sm text-moss">{error}</p>}
            {notice && <p data-testid="auth-notice" className="text-sm text-charcoal">{notice}</p>}

            <button data-testid="auth-submit" onClick={submit} disabled={busy}
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-charcoal py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60">
              {mode === "forgot-password" ? "Send reset link"
                : mode === "forgot-id" ? "Email my sign-in ID"
                : tab === "signin" ? "Sign in"
                : tab === "signup" ? "Create my account"
                : otpSent ? "Verify & continue" : "Send code by SMS"}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            <div className="flex justify-between pt-2 text-xs text-ash">
              {!mode ? (
                <>
                  <button data-testid="forgot-id-link" onClick={() => { setMode("forgot-id"); setError(""); setNotice(""); }}
                    className="transition-colors duration-300 hover:text-charcoal">Forgot ID?</button>
                  <button data-testid="forgot-password-link" onClick={() => { setMode("forgot-password"); setError(""); setNotice(""); }}
                    className="transition-colors duration-300 hover:text-charcoal">Forgot password?</button>
                </>
              ) : (
                <button data-testid="back-to-signin" onClick={() => { setMode(null); setError(""); setNotice(""); }}
                  className="transition-colors duration-300 hover:text-charcoal">Back to sign in</button>
              )}
            </div>
          </div>

          <p className="mt-10 text-center text-xs font-light text-ash">
            <Link to="/" data-testid="auth-continue-guest" className="underline underline-offset-4 hover:text-charcoal">
              Continue browsing as a guest
            </Link>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
